import React, { useEffect, useRef, useState } from 'react'
import _ from 'underscore'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import { Panel } from 'components/Panel'
import ColumnGridPanel from 'components/ColumnGridPanel'

import IndoorUnit from 'pages/Device/SystemStatusPanel/IndoorUnit'
import LastUpdated from 'pages/Device/LastUpdated'
import OutdoorUnit from 'pages/Device/SystemStatusPanel/OutdoorUnit'
import Settings from 'pages/Device/SystemStatusPanel/Settings'
import Status from 'pages/Device/SystemStatusPanel/Status'
import ThermostatBox from 'pages/Device/SystemStatusPanel/ThermostatBox'
import ZoningInfo from 'pages/Device/SystemStatusPanel/ZoningInfo'
import useDefaultFirmwareLoads from 'hooks/useDefaultFirmwareLoads'

import { NoData } from 'styles/shared'
import { color } from 'styles/colors'

type SystemStatusPanelProps = {
  report?: Record<string, unknown> | null
  hasAwairDevice: boolean
  deviceModel: string
  showAll: boolean
}

const SystemStatusPanel = ({ report = null, hasAwairDevice, showAll, deviceModel }: SystemStatusPanelProps) => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(showAll ?? false)
  const [hideShowMore, setHideShowMore] = useState(showAll ?? true)
  const gridRef = useRef<HTMLDivElement | null>(null)
  useDefaultFirmwareLoads()

  useEffect(() => {
    if (gridRef.current && !showAll) {
      const maxNumberOfGridFields = _.max(Array.from(gridRef.current.firstElementChild?.children ?? []).map((listItem) => (listItem as HTMLElement).children.length))
      setHideShowMore(maxNumberOfGridFields < 13) // show show more button if elements count is more than 12 including column header in any column
    }
  }, [report, showAll])

  return (
    <S.Panel pingdomId="thermostat-current-status-container" id="system-status" scrollToSection>
      <Panel.Header>
        <Panel.Title>System Status</Panel.Title>
        {report && <LastUpdated date={report.lastUpdatedAt as Date | null | undefined} /> }
      </Panel.Header>
      {
        !report
          ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          : (
            <S.PanelContainer>
              <div ref={gridRef}>
                <S.ColumnGridPanel isPanelExpanded={isPanelExpanded}>
                  {
                    !_.isEmpty(report.zones as unknown[]) &&
                    (report.zones as unknown[]).length === 1 &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    <ThermostatBox {...(report.zones as any[])[0]} mode={(report.systemMode as string) || (report.zones as any[])[0].reqZoneMode} outdoorTemperature={report.outdoorTemperature} hasAwairDevice={hasAwairDevice} />
                  }

                  <Settings report={report} deviceModel={deviceModel} />
                  <Status report={report} />
                  <OutdoorUnit report={report} />
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  <IndoorUnit report={report as any} />
                  {
                    !_.isEmpty(report.zones as unknown[]) &&
                    (report.zones as unknown[]).length > 1 &&
                    <ZoningInfo report={report} />
                  }
                </S.ColumnGridPanel>
              </div>

              {
                !hideShowMore && (
                  <Button
                    decoration="secondary"
                    text={isPanelExpanded ? 'Show Less' : 'Show More'}
                    styles={{ button: { marginTop: 10 } }}
                    onPress={() => setIsPanelExpanded(!isPanelExpanded)}
                  />
                )
              }
            </S.PanelContainer>
          )
      }
    </S.Panel>
  )
}

export default SystemStatusPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
    background-color: ${color('paneBackground')};
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  PanelContainer: styled.div`
    display: flex;
    flex-direction: column;
  `,
  ColumnGridPanel: styled(ColumnGridPanel).withConfig({
    shouldForwardProp: (prop) => !['isPanelExpanded'].includes(prop)
  })<{ isPanelExpanded?: boolean }>`
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));

    > div > :nth-child(n+12) {
      display: ${({ isPanelExpanded }) => isPanelExpanded ? 'gird' : 'none'};
    }
  `
}
